import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { formatCurrency, formatDate } from '../../utils/estimationEngine';

const SavedProjects = ({
  projects,
  onSelect,
  onDelete,
  onBack,
  onStartNew,
  language = 'en'
}) => {
  const content = {
    en: {
      title: 'Saved Projects',
      subtitle: 'Select a project to view or continue editing',
      noProjects: 'No saved projects yet',
      noProjectsDesc: 'Start a new estimation to create your first project.',
      startNew: 'Start New Estimation',
      back: 'Back',
      sqft: 'sq ft',
      deleteConfirm: 'Delete this project?'
    },
    my: {
      title: 'Projek Tersimpan',
      subtitle: 'Pilih projek untuk lihat atau teruskan',
      noProjects: 'Tiada projek tersimpan lagi',
      noProjectsDesc: 'Mulakan anggaran baru untuk cipta projek pertama anda.',
      startNew: 'Mulakan Anggaran Baru',
      back: 'Kembali',
      sqft: 'kaki persegi',
      deleteConfirm: 'Padam projek ini?'
    }
  };

  const t = content[language] || content.en;

  const handleDelete = (e, projectId) => {
    e.stopPropagation();
    if (window.confirm(t.deleteConfirm)) {
      onDelete(projectId);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Box>
      </Box>

      {projects.length === 0 ? (
        // Empty state
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
            {t.noProjects}
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            {t.noProjectsDesc}
          </Typography>
          <Button
            variant="contained"
            onClick={onStartNew}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.startNew}
          </Button>
        </Paper>
      ) : (
        // Project list
        <>
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <List disablePadding>
              {projects.map((project, index) => (
                <React.Fragment key={project.id}>
                  {index > 0 && <Divider />}
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => onSelect(project)}
                      sx={{ py: 2, px: 3 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {project.inputs?.projectName ||
                              (language === 'my'
                                ? project.inputs?.houseTypeLabelMy
                                : project.inputs?.houseTypeLabel) ||
                              'Untitled Project'}
                          </Typography>
                        }
                        secondary={
                          <Box component="span">
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              {project.inputs?.builtUpArea?.toLocaleString()} {t.sqft} •{' '}
                              {formatCurrency(project.costs?.average)}
                            </Typography>
                            <br />
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.disabled"
                            >
                              {formatDate(project.savedAt || project.generatedAt, language)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={(e) => handleDelete(e, project.id)}
                          sx={{ color: 'text.disabled' }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>

          <Button
            fullWidth
            variant="outlined"
            onClick={onStartNew}
            startIcon={<AddIcon />}
            sx={{
              mt: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.startNew}
          </Button>
        </>
      )}
    </Box>
  );
};

export default SavedProjects;
